import { Check } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import BaseCarousel, { CARD_WIDTH } from '@/components/shared/Carousel';
import FadeIn from '@/components/shared/FadeIn';
import { Button } from '@/components/ui/button';
import { LG_QUERY, MD_QUERY, useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import Placeholder from '../cards/Placeholder';
import Skeleton from '../cards/Skeleton';
import Skip from '../cards/Skip';

const MIN_CARDS = 5;
const DESKTOP_CARD_WIDTH = 200;
const PLACEHOLDER_KEYS = ['placeholder-a', 'placeholder-b', 'placeholder-c', 'placeholder-d'];

interface PlaceholderLimits {
	base: number;
	md: number;
	lg: number;
}

interface CarouselProps<TItem> {
	title: string;
	subtitle: React.ReactNode;
	carouselKeyPrefix: string;
	items: TItem[];
	expectedNumQuotes: number | null;
	getItemId: (item: TItem) => string;
	renderItem: (item: TItem) => React.ReactNode;
	selectedId: string | null;
	isBusy: boolean;
	isSaving: boolean;
	isLoading: boolean;
	isLoadingDisplayed: boolean;
	error: boolean;
	productName: string;
	fetchErrorLabel: string;
	noItemsLabel: string;
	confirmLabel: string;
	skipLabel: string;
	onRetry: () => void;
	onSkip: () => void;
	onConfirm: () => void;
	onBack: () => void;
	showNavigation?: boolean;
	headerAction?: React.ReactNode;
	placeholderLimits?: PlaceholderLimits;
	desktopVisibleCards?: number;
	renderSkeleton?: (showSpinner?: boolean) => React.ReactNode;
}

function Carousel<TItem>({
	title,
	subtitle,
	carouselKeyPrefix,
	items,
	expectedNumQuotes,
	getItemId,
	renderItem,
	selectedId,
	isBusy,
	isSaving,
	isLoading,
	isLoadingDisplayed,
	error,
	productName,
	fetchErrorLabel,
	noItemsLabel,
	confirmLabel,
	skipLabel,
	onRetry,
	onSkip,
	onConfirm,
	onBack,
	showNavigation = true,
	headerAction,
	placeholderLimits = { base: 1, md: 2, lg: 3 },
	desktopVisibleCards = MIN_CARDS,
	renderSkeleton = (showSpinner?: boolean) => <Skeleton showSpinner={showSpinner} />
}: CarouselProps<TItem>): React.JSX.Element {
	const { t } = useTranslation();
	const isMd = useMediaQuery(MD_QUERY);
	const isLg = useMediaQuery(LG_QUERY);

	const maxPlaceholders = isLg
		? placeholderLimits.lg
		: isMd
			? placeholderLimits.md
			: placeholderLimits.base;
	// Number of skeleton slots still needed while quotes trickle in.
	// Requires at least one real item so spinners only appear alongside
	// arriving cards, never in the initial full-skeleton state.
	const pendingSkeletonCount =
		isLoading && !error && expectedNumQuotes !== null && items.length > 0
			? Math.max(0, expectedNumQuotes - items.length)
			: 0;

	const placeholderCount =
		!isLoadingDisplayed && !error && items.length > 0
			? Math.min(
					maxPlaceholders,
					Math.max(0, (isLg ? desktopVisibleCards : MIN_CARDS) - items.length - 1)
				)
			: 0;
	// Match the number of skeleton cards to what will be visible at the end,
	// so the layout doesn't jump between the loading and loaded states.
	const skeletonCount = isLg ? desktopVisibleCards : MIN_CARDS;
	// True when a new fetch just started but stale items are still in state
	// (e.g. startLoading() was called but React hasn't batched all setters yet).
	// pendingSkeletonCount > 0 means quotes are genuinely trickling in, so only
	// treat items as stale when there are no pending slots — that combo means we
	// are at the very beginning of a refetch with leftover data from the previous
	// load. Treat this the same as the empty-state so old cards never flash.
	const isStaleRefetch = isLoading && items.length > 0 && pendingSkeletonCount === 0;
	const showInitialSkeletons =
		(isLoading || isLoadingDisplayed) &&
		!error &&
		(items.length === 0 || isStaleRefetch) &&
		pendingSkeletonCount === 0;
	// Only used by BaseCarousel to reset scroll position when content changes.
	const carouselKey = !isLoadingDisplayed
		? `${carouselKeyPrefix}-${items.length}-${placeholderCount}`
		: `${carouselKeyPrefix}-loading`;

	return (
		<div className="flex flex-col justify-center select-none">
			<div className="px-2 flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
				<div>
					<h3 className="text-left text-2xl md:text-[1.6875rem] font-bold mb-2">{title}</h3>
					<p className="text-left text-base md:text-lg text-muted-foreground leading-relaxed">
						{subtitle}
					</p>
				</div>

				{headerAction}
			</div>

			<FadeIn>
				<div className="pb-6 md:pb-5">
					<BaseCarousel
						key={carouselKey}
						cardWidthPx={isLg ? DESKTOP_CARD_WIDTH : isMd ? CARD_WIDTH : 260}
						testId={`${carouselKeyPrefix}-carousel`}
					>
						{showInitialSkeletons &&
							Array.from({ length: skeletonCount }, (_, i) => `skeleton-init-${i}`).map((key) => (
								<div key={key} className="min-w-0 w-full h-full">
									{renderSkeleton()}
								</div>
							))}
						{error && (
							<div className="flex flex-col items-center justify-center my-14 w-full min-w-full text-center">
								<p className="text-destructive mb-4">{fetchErrorLabel}</p>
								<Button type="button" variant="outline" onClick={onRetry}>
									{t('common.retry')}
								</Button>
							</div>
						)}
						{!isLoading && !isLoadingDisplayed && !error && items.length === 0 && (
							<div className="flex items-center justify-center my-14 w-full min-w-full text-muted-foreground">
								{noItemsLabel}
							</div>
						)}
						{!error && items.length > 0 && !isStaleRefetch && isMd && (
							<div key="skip" className="min-w-0 w-full h-full">
								<Skip
									productName={productName}
									isSelected={selectedId === null}
									isDisabled={isBusy}
									onSelect={onSkip}
								/>
							</div>
						)}

						{!error &&
							!isStaleRefetch &&
							items.map((item) => (
								<div key={getItemId(item)} className="min-w-0 w-full h-full">
									{renderItem(item)}
								</div>
							))}
						{pendingSkeletonCount > 0 &&
							Array.from(
								{ length: pendingSkeletonCount },
								(_, i) => `${carouselKeyPrefix}-pending-${i}`
							).map((key) => (
								<div key={key} className="min-w-0 w-full h-full">
									{renderSkeleton(true)}
								</div>
							))}
						{PLACEHOLDER_KEYS.slice(0, placeholderCount).map((key) => (
							<div key={key} className="min-w-0 w-full h-full">
								<Placeholder />
							</div>
						))}
						{!error && items.length > 0 && !isStaleRefetch && !isMd && (
							<div key="skip" className="min-w-0 w-full h-full">
								<Skip
									productName={productName}
									isSelected={selectedId === null}
									isDisabled={isBusy}
									onSelect={onSkip}
								/>
							</div>
						)}
					</BaseCarousel>
				</div>
			</FadeIn>

			{showNavigation && (
				<div
					className={cn(
						'text-center pt-1 pb-2 lg:py-6 px-2 sm:px-0 flex flex-col-reverse sm:flex-row sm:justify-center gap-2 sm:gap-0 transition-opacity duration-200',
						!isLoading && !isLoadingDisplayed ? 'opacity-100' : 'opacity-0 pointer-events-none'
					)}
					style={{ transitionDelay: !isLoading && !isLoadingDisplayed ? '150ms' : '0ms' }}
				>
					<Button
						type="button"
						variant="outline"
						size="xl"
						className="font-bold w-full sm:w-auto sm:mr-3 lg:mr-4"
						onClick={onBack}
						data-testid="application-previous-button"
					>
						{t('application.navigation.previous')}
					</Button>
					<Button
						type="button"
						variant={selectedId ? 'default' : 'outline'}
						size="xl"
						className="font-bold w-full sm:w-auto"
						disabled={isBusy || isSaving}
						loading={isSaving}
						onClick={onConfirm}
						iconRight={selectedId ? <Check className="w-4 h-4" /> : undefined}
						data-testid="application-next-button"
					>
						{selectedId ? confirmLabel : skipLabel}
					</Button>
				</div>
			)}
		</div>
	);
}

export default Carousel;
